/**
 * Opening-hours persistence for the onboarding wizard (#1498).
 *
 * `PATCH /tenants/me/settings` replaces `businessHours` wholesale, so this
 * step always sends a complete week. It never includes workshop identity,
 * VAT, or labour-rate keys — those belong to step 1 and a full payload
 * would overwrite them the same way #1795 did.
 */

import { api } from '@/lib/api';
import type { WorkshopOpeningHours } from '@/types';

const DAY_KEYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const satisfies readonly (keyof WorkshopOpeningHours)[];

const WEEKDAY_DEFAULT = { open: '08:00', close: '17:00' };

export const DEFAULT_OPENING_HOURS: WorkshopOpeningHours = {
  monday: { ...WEEKDAY_DEFAULT },
  tuesday: { ...WEEKDAY_DEFAULT },
  wednesday: { ...WEEKDAY_DEFAULT },
  thursday: { ...WEEKDAY_DEFAULT },
  friday: { ...WEEKDAY_DEFAULT },
  saturday: null,
  sunday: null,
};

function parseDay(value: unknown): { open: string; close: string } | null {
  if (value === null) return null;
  if (typeof value !== 'object') return { ...WEEKDAY_DEFAULT };
  const rec = value as { open?: unknown; close?: unknown };
  if (typeof rec.open === 'string' && typeof rec.close === 'string') {
    return { open: rec.open, close: rec.close };
  }
  return { ...WEEKDAY_DEFAULT };
}

function cloneDay(
  value: { open: string; close: string } | null,
): { open: string; close: string } | null {
  return value ? { open: value.open, close: value.close } : null;
}

export function normalizeOpeningHours(raw: unknown): WorkshopOpeningHours {
  const src =
    typeof raw === 'object' && raw !== null && !Array.isArray(raw)
      ? (raw as Record<string, unknown>)
      : {};

  const hours: WorkshopOpeningHours = {
    monday: cloneDay(DEFAULT_OPENING_HOURS.monday),
    tuesday: cloneDay(DEFAULT_OPENING_HOURS.tuesday),
    wednesday: cloneDay(DEFAULT_OPENING_HOURS.wednesday),
    thursday: cloneDay(DEFAULT_OPENING_HOURS.thursday),
    friday: cloneDay(DEFAULT_OPENING_HOURS.friday),
    saturday: cloneDay(DEFAULT_OPENING_HOURS.saturday),
    sunday: cloneDay(DEFAULT_OPENING_HOURS.sunday),
  };
  for (const day of DAY_KEYS) {
    if (day in src) {
      hours[day] = parseDay(src[day]);
    }
  }
  return hours;
}

export async function commitHoursStep(hours: WorkshopOpeningHours): Promise<void> {
  await api.patch('/tenants/me/settings', {
    businessHours: normalizeOpeningHours(hours),
  });
}
